/**
 * KAN-834: блок "Итог" карточки клиента в приложении менеджера: взять в работу,
 * Перезвонить (дата и время), Отказ (с подтверждением), Успех, комментарий.
 * KAN-852: "Дозвонился" заменён кнопками "Взять в работу" / "Назначить"
 * (MobileWorkButtons, по роли и правам).
 * Логика в useMobileOutcome; окна "Взять в работу" и "Успех" общие с дашбордом.
 * KAN-858: состояние итога приходит от страницы (кнопки взятия в работу
 * есть и в блоке "Что сделать", окна одни).
 * KAN-880: вместо отдельных "Успех" и "Отказ" одна кнопка "Отработано" с
 * выбором "Успех" / "Не успех" (WorkOutcomeChoice, как в блоке внимания);
 * "Не успех" = прежний отказ с подтверждением.
 * KAN-911: "Отработано" только у взятого в работу (outcome.canMarkOutcome).
 * KAN-898: "В мусор" (с подтверждением), после успеха назад; контакт уже в мусоре -
 * метка "В мусоре" и "Вернуть" владельцу (ClientJunkControls).
 * KAN-924: закрытый исход (lost / won) - "Вернуть в работу" (ClientReopenButton), карточка остаётся.
 * KAN-940: комментарий без кнопки, сохраняется сам (useAutosaveText): статус
 * "Сохраняется..." / "Сохранено" / "Не сохранено" (с текстом сервера) и "Повторить";
 * сбой сохранения после ухода с карточки - тост.
 * KAN-944: несохранённый комментарий лежит черновиком клиента (clientCommentDraftKey):
 * его видно, если клиента взял другой менеджер.
 * KAN-955: "Работа идёт" у клиента с причиной "Нет движения" (outcome.canContinueWork).
 * KAN-960: "Следующий контакт" (звонок или встреча, та же модалка, что в кабинете).
 */
import { useState } from 'react';
import { Hourglass } from 'lucide-react';
import { toast } from 'sonner';
import { AttentionDoneDialog } from '@/components/dashboard/AttentionDoneDialog';
import { AttentionSuccessFlow } from '@/components/dashboard/AttentionSuccessDialog';
import { WorkOutcomeChoice } from '@/components/dashboard/WorkOutcomeChoice';
import { MobileRefuseConfirm } from '@/components/mobile/MobileRefuseConfirm';
import { MobileWorkButtons } from '@/components/mobile/MobileWorkButtons';
import { ClientJunkControls } from '@/components/clients/ClientJunkButtons';
import { ClientReopenButton } from '@/components/clients/ClientReopenButton';
import { NextContactAction } from '@/components/clients/NextContactAction';
import { clientNextContactTarget } from '@/lib/nextContact';
import { useAutosaveText, type AutosaveStatus } from '@/hooks/useAutosaveText';
import { useMobileBack } from '@/hooks/useMobileBack';
import type { MobileOutcome } from '@/hooks/useMobileOutcome';
import { clientCommentDraftKey } from '@/lib/composerDraft';
import { cn } from '@/lib/utils';
import { WORK_CONTINUES_LABEL } from '@/lib/workContinues';
import type { Client } from '@/types/client';

const BTN = 'min-h-[48px] rounded-xl px-4 text-base font-medium disabled:opacity-50';
const COMMENT_NOT_SAVED = 'Комментарий не сохранён';
const onCommentLost = () => toast.error(COMMENT_NOT_SAVED);

interface CommentStatusProps {
  status: AutosaveStatus;
  error: string | null;
  onRetry: () => void;
}

function CommentStatus({ status, error, onRetry }: CommentStatusProps): JSX.Element | null {
  if (status === 'idle') return null;
  if (status === 'error') {
    return (
      <p className="flex flex-wrap items-center gap-2 text-sm text-red-700" aria-live="polite">
        <span>Не сохранено</span>
        {error && <span data-testid="mobile-comment-error">{error}</span>}
        <button type="button" onClick={onRetry} className="font-medium underline">
          Повторить
        </button>
      </p>
    );
  }
  return (
    <p className="text-sm text-gray-500" aria-live="polite">
      {status === 'saving' ? 'Сохраняется...' : 'Сохранено'}
    </p>
  );
}

export function MobileOutcomeBlock({ client, outcome }: { client: Client; outcome: MobileOutcome }): JSX.Element {
  const [callbackOpen, setCallbackOpen] = useState(false);
  const [callbackAt, setCallbackAt] = useState(() => outcome.callbackInitial);
  const comment = useAutosaveText({
    saved: client.crm_comment ?? '',
    save: outcome.saveCommentQuiet,
    onFailAfterUnmount: onCommentLost,
    draftKey: clientCommentDraftKey(client.id),
  });
  const busy = outcome.pending || outcome.saving;
  const back = useMobileBack();

  return (
    <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-4" data-testid="mobile-outcome">
      <h2 className="text-base font-semibold text-gray-900">Итог</h2>
      <MobileWorkButtons work={outcome.work} disabled={busy} testIdPrefix="mobile-client-work" />
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            // KAN-840: пояс организации мог прийти после монтирования: поле
            // заполняется заново при каждом открытии.
            if (!callbackOpen) setCallbackAt(outcome.callbackInitial);
            setCallbackOpen(!callbackOpen);
          }}
          className={cn(BTN, 'border border-gray-300 bg-white text-gray-900')}
        >
          Перезвонить
        </button>
        {/* KAN-960: та же модалка "Следующий контакт", что в кабинете. */}
        <NextContactAction
          target={clientNextContactTarget(client)}
          surface="app"
          disabled={busy}
          className={cn(BTN, 'h-auto border border-gray-300 bg-white text-gray-900')}
        />
        {outcome.canMarkOutcome && (
          <WorkOutcomeChoice
            disabled={busy}
            onChoose={(choice) => (choice === 'success' ? outcome.markSuccess() : outcome.askRefuse())}
            className="contents"
            triggerClassName={cn(BTN, 'bg-green-600 text-white')}
            menuClassName="col-span-2 grid grid-cols-2 gap-2"
            optionClassName={cn(BTN, 'border border-gray-300 bg-white')}
          />
        )}
        {outcome.canContinueWork && (
          <button
            type="button"
            disabled={busy}
            onClick={outcome.continueWork}
            data-testid="mobile-work-continues"
            className={cn(BTN, 'col-span-2 inline-flex items-center justify-center gap-2 border border-gray-300 bg-white text-gray-900')}
          >
            <Hourglass className="h-4 w-4" aria-hidden="true" />
            {WORK_CONTINUES_LABEL}
          </button>
        )}
      </div>

      {callbackOpen && (
        <div className="space-y-2 rounded-lg bg-gray-50 p-3" data-testid="mobile-callback">
          <label className="block text-sm text-gray-700">
            {outcome.callbackLabel}
            <input
              type="datetime-local"
              value={callbackAt}
              onChange={(e) => setCallbackAt(e.target.value)}
              className="mt-1 h-12 w-full rounded-lg border border-gray-300 bg-white px-3 text-base"
            />
          </label>
          <button
            type="button"
            disabled={busy}
            onClick={() => void outcome.scheduleCallback(callbackAt)}
            className={cn(BTN, 'w-full bg-blue-600 text-white')}
          >
            Сохранить перезвон
          </button>
        </div>
      )}

      {outcome.refuseAsked && <MobileRefuseConfirm onConfirm={outcome.confirmRefuse} onCancel={outcome.cancelRefuse} />}

      <label className="block text-sm text-gray-700">
        Комментарий
        <textarea
          value={comment.value}
          onChange={(e) => comment.setValue(e.target.value)}
          onBlur={comment.onBlur}
          rows={3}
          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-base"
        />
      </label>
      <CommentStatus status={comment.status} error={comment.error} onRetry={comment.retry} />

      <ClientReopenButton
        clientId={client.id}
        outcome={client.outcome}
        junkAt={client.junk_at}
        className={cn(BTN, 'w-full')}
      />

      {/* KAN-898: убрать контакт из всех списков, после успеха назад. */}
      <ClientJunkControls
        clientId={client.id}
        junkAt={client.junk_at}
        onJunked={back}
        junkClassName={cn(BTN, 'w-full')}
      />

      {outcome.error && (
        <p className="text-sm text-red-700" role="alert">
          {outcome.error}
        </p>
      )}

      <AttentionDoneDialog {...outcome.work.dialog} />
      <AttentionSuccessFlow success={outcome.success} />
    </section>
  );
}
